import { useToast } from 'primevue/usetoast';

/**
 * Composable for showing enhanced notifications with expandable details and copy functionality.
 * Works for all severity levels: error, warn, success, info.
 *
 * Usage:
 *   const { notifyError, notifyWarning, notifySuccess, notifyInfo } = useNotification()
 *   notifyError('Operation failed', error)
 *   notifyWarning('Partial success', 'Some items were skipped')
 *   notifySuccess('Completed', 'All items processed successfully')
 */
export function useNotification() {
  const toast = useToast();

  /**
   * Extract message string from various error/message formats
   */
  const extractMessage = (input) => {
    if (!input) {return '';}

    if (typeof input === 'string') {
      return input;
    }

    if (input instanceof Error) {
      let message = input.message;
      if (input.stack && process.env.NODE_ENV === 'development') {
        message += '\n\nStack trace:\n' + input.stack;
      }
      return message;
    }

    if (input.message) {
      return input.message;
    }

    if (input.error) {
      return input.error;
    }

    try {
      return JSON.stringify(input, null, 2);
    } catch {
      return String(input);
    }
  };

  /**
   * Show an expandable notification toast
   * @param {'error'|'warn'|'success'|'info'} severity - Notification severity
   * @param {string} summary - Short title
   * @param {Error|string|object} [detail] - Detailed message or error object
   */
  const notify = (severity, summary, detail = null) => {
    const fullDetail = extractMessage(detail);

    toast.add({
      severity,
      summary,
      detail: fullDetail.length > 150 ? fullDetail.substring(0, 150) + '...' : fullDetail,
      group: 'expandable',
      data: {
        fullDetail
      }
    });
  };

  /**
   * Show an error notification with expandable details
   * @param {string} summary - Short error title
   * @param {Error|string|object} [error] - Error object or message
   */
  const notifyError = (summary, error = null) => {
    notify('error', summary, error);
  };

  /**
   * Show a warning notification with expandable details
   * @param {string} summary - Short warning title
   * @param {string|object} [detail] - Warning details
   */
  const notifyWarning = (summary, detail = null) => {
    notify('warn', summary, detail);
  };

  /**
   * Show a success notification with expandable details
   * @param {string} summary - Short success title
   * @param {string|object} [detail] - Success details
   */
  const notifySuccess = (summary, detail = null) => {
    notify('success', summary, detail);
  };

  /**
   * Show an info notification with expandable details
   * @param {string} summary - Short info title
   * @param {string|object} [detail] - Info details
   */
  const notifyInfo = (summary, detail = null) => {
    notify('info', summary, detail);
  };

  /**
   * Show a simple (non-expandable) toast for short messages
   * @param {'error'|'warn'|'success'|'info'} severity - Notification severity
   * @param {string} summary - Short title
   * @param {string} [detail] - Short detail message
   * @param {number} [life=3000] - Toast duration in ms
   */
  const notifySimple = (severity, summary, detail = '', life = 3000) => {
    toast.add({
      severity,
      summary,
      detail,
      life
    });
  };

  return {
    notify,
    notifyError,
    notifyWarning,
    notifySuccess,
    notifyInfo,
    notifySimple
  };
}
